'use client';
import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'dark'
  },
  cssVariables: true,
  typography: {
    fontFamily: 'var(--font-roboto)',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus': {
          WebkitTextFillColor: 'unset',
          caretColor: 'rgba(var(--textLight))',
          WebkitBoxShadow: 'unset',
          boxShadow: 'unset',
          transition: 'unset',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          maxWidth: "100%",
          width: "max-content",
          backgroundColor: "rgba(var(--surface))",
          color: "rgba(var(--textLight))",
          fontSize: "14px",
          fontWeight: "500",
          lineHeight: "18px",
          borderRadius: "16px",
          padding: "12px",
          boxShadow: "0 0 15px 0 rgba(var(--strokeDefault), 0.5)",
          backdropFilter: "blur(12.5px)",
        },
        arrow: {
          color: "rgba(var(--strokeDivider))",
        },
        popper: {
          '&.constraints .Mui-tooltip': {
            backgroundColor: "rgba(var(--surface))",
            padding: "var(--padding12)",
            boxShadow: "0 0 15px 0 rgba(var(--strokeDefault), 0.50)",
            borderRadius: "1rem",
            border: "1px solid rgba(var(--strokeDefault))",
            "backdropFilter": "blur(15px)"
          }
        }
      },
      defaultProps: {
        arrow: true,
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          /* width: "420px", */
          color: "rgba(var(--textLight))",
          paddingInline: "20px",
          font: 'unset',
          "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
            WebkitTextFillColor: "unset",
            caretColor: "rgba(var(--textLight))",
            WebkitBoxShadow: "unset",
            boxShadow: "unset",
            borderRadius: "inherit",
            backgroundClip: "text",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(var(--strokeDefault))",
            borderRadius: "1rem",
            /* width: "420px",*/
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            "@media (hover: hover) and (pointer: fine)": {
              borderColor: "rgba(var(--strokeDefault))",
              borderWidth: 2,
              backgroundColor: "rgba(var(--fieldHoverBg))",
            }
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(var(--strokeDefault))",
            borderWidth: 2,
            backgroundColor: "rgba(var(--fieldHoverBg))",
            boxShadow: "none",
          },
          "& input, & textarea": {
            width: '100%',
            zIndex: "2",
            padding: "14px 0px",
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(var(--errorMessage))",
          },
          "&.MuiInputBase-multiline": {
            flexDirection: "column"
          }
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "rgba(var(--textLight))",
          transform: "translate(14px, 14px) scale(1)",
          "&.Mui-error": {
            color: "rgba(var(--errorMessage))", // color del label en error
          },
          "&.Mui-focused" : {
            color: "rgba(var(--textLight))",
            "&.Mui-error": {
              color: "rgba(var(--errorMessage))", // color del label en error
            }
          }
        },
        shrink: {
          transform: "translate(14px, -9px) scale(0.75)"
        }
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          zIndex: 2, // el valor que quieras
          position: "relative", // asegúrate de que tome el z-index
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: 'var(--font-manrope)',
          fontSize: "16px",
          color: "rgba(var(--textLight))",
          "&.Mui-error": {
            color: "rgba(var(--errorMessage))",
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          color: "rgba(var(--textLight))",
          paddingInline: "20px",
          font: "unset",
          borderRadius: "1rem",
          /* overflow: 'hidden',*/
          "&:hover": {
            "@media (hover: hover) and (pointer: fine)": {
              backgroundColor: "rgba(var(--fieldHoverBg))",
            },
          },
          "&.Mui-focused:not(.Mui-error)": {
            backgroundColor: "rgba(var(--fieldHoverBg))",
          },
          "& .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: "rgba(var(--strokeDefault))",
          },
          "&:hover .MuiPickersOutlinedInput-notchedOutline": {
            "@media (hover: hover) and (pointer: fine)": {
              borderColor: "rgba(var(--strokeDefault))",
              borderWidth: 2,
            },
          },
          "&.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: 'rgba(var(--strokeDefault))',
            borderWidth: 2,
            boxShadow: 'none',
          },
          "& .MuiPickersInputBase-input": {
            width: '100%',
            zIndex: "2",
            padding: "14px 0px",
            color: "rgba(var(--textLight))",
          },
          "& .MuiPickersSectionList-root": {
            padding: "14px 0px",
          },
          "&.Mui-error .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: "rgba(var(--errorMessage))",
          },
        },
      },
    },
    MuiPickerPopper: {
      styleOverrides: {
        root: {
          // to properly hide the color in the corners of the DatePicker
          borderRadius: "var(--padding28)",
          overflow: "hidden"
        },
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // to properly hide the color in the corners of the DatePicker
          borderRadius: "var(--padding28)",
          backgroundImage: 'none',
          overflow: "hidden"
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          fontFamily: "var(--font-manrope)",
          color: "rgba(var(--textLight))",
          borderRadius: "var(--padding28)",
          border: "1px solid rgba(var(--strokeDefault))",
          backgroundColor: "rgba(var(--surface))",
          overflow: "hidden"
        }
      }
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          fontFamily: "var(--font-manrope)",
          color: "rgba(var(--textLight))",
        }
      }
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          outline: "none",
          color: "rgba(var(--textLight))",
          "&:focus": {
            color: "unset",
            backgroundColor: "unset",
            border: "none"
          },
          "&:focus-visible": {
            color: "rgba(var(--textHoverHighlight))",
            border: "1px solid rgba(var(--textHoverHighlight))",
            backgroundColor: "transparent"
          },
          "&:hover": {
           "@media (hover: hover) and (pointer: fine)": {
              color: "rgba(var(--textHoverHighlight))",
              border: "1px solid rgba(var(--textHoverHighlight))",
              backgroundColor: "transparent"
            }
          },
          "&:active": {
            color: "rgba(var(--textHoverHighlight))",
            border: "1px solid rgba(var(--textHoverHighlight))",
            backgroundColor: "rgba(var(--primary))"
          },
          "&.Mui-selected": {
            color: "rgba(var(--background))",
            border: "1px solid rgba(var(--textHoverHighlight))",
            backgroundColor: "rgba(var(--textHoverHighlight))",
            "&:focus": {
              color: "rgba(var(--background))",
              border: "1px solid rgba(var(--textHoverHighlight))",
              backgroundColor: "rgba(var(--textHoverHighlight))",
            },
            "&:focus-visible": {
              color: "rgba(var(--textHoverHighlight))",
              border: "1px solid rgba(var(--textHoverHighlight))",
              backgroundColor: "rgba(var(--primary2))"
            },
            "&:hover": {
              "@media (hover: hover) and (pointer: fine)": {
                color: "rgba(var(--textHoverHighlight))",
                border: "1px solid rgba(var(--textHoverHighlight))",
                backgroundColor: "rgba(var(--primary2))"
              }
            }
          }
        },
        today: {
          outline: "none",
          color: "rgba(var(--textLight))",
          border: "1px solid rgba(var(--primary))",
          backgroundColor: "rgba(var(--primary))",
          "&:not(.Mui-selected)": {
            color: "rgba(var(--textLight))",
            border: "1px solid rgba(var(--primary))",
            backgroundColor: "rgba(var(--primary))",
            "&:hover": {
              "@media (hover: hover) and (pointer: fine)": {
                color: "rgba(var(--textHoverHighlight))",
                border: "1px solid rgba(var(--textHoverHighlight))",
                backgroundColor: "transparent"
              }
            }
          },
          "&.Mui-selected": {
            color: "rgba(var(--background))",
            border: "1px solid rgba(var(--textHoverHighlight))",
            backgroundColor: "rgba(var(--textHoverHighlight))",
            "&:hover": {
              "@media (hover: hover) and (pointer: fine)": {
                color: "rgba(var(--textHoverHighlight))",
                border: "1px solid rgba(var(--textHoverHighlight))",
                backgroundColor: "rgba(var(--primary2))",
              }
            }
          },
          "&:focus:not(.Mui-selected)": {
            color: "rgba(var(--textLight))",
            border: "1px solid rgba(var(--primary))",
            backgroundColor: "rgba(var(--primary))",
            "&:hover": {
              "@media (hover: hover) and (pointer: fine)": {
                color: "rgba(var(--textHoverHighlight))",
                border: "1px solid rgba(var(--textHoverHighlight))",
                backgroundColor: "transparent"
              }
            }
          },
          "&:focus.Mui-selected": {
            color: "rgba(var(--background))",
            border: "1px solid rgba(var(--textHoverHighlight))",
            backgroundColor: "rgba(var(--textHoverHighlight))",
            "&:hover": {
              "@media (hover: hover) and (pointer: fine)": {
                color: "rgba(var(--textHoverHighlight))",
                border: "1px solid rgba(var(--textHoverHighlight))",
                backgroundColor: "rgba(var(--primary2))",
              }
            }
          },
          "&:focus-visible:not(.Mui-selected)": {
            color: "rgba(var(--textHoverHighlight))",
            border: "1px solid rgba(var(--textHoverHighlight))",
            backgroundColor: "transparent",
            "&:hover": {
              "@media (hover: hover) and (pointer: fine)": {
                color: "rgba(var(--textHoverHighlight))",
                border: "1px solid rgba(var(--textHoverHighlight))",
                backgroundColor: "transparent",
              },
            },
          },
          "&:focus-visible.Mui-selected": {
            color: "rgba(var(--textHoverHighlight))",
            border: "1px solid rgba(var(--textHoverHighlight))",
            backgroundColor: "rgba(var(--primary2))",
            "&:hover": {
              "@media (hover: hover) and (pointer: fine)": {
                color: "rgba(var(--textHoverHighlight))",
                border: "1px solid rgba(var(--textHoverHighlight))",
                backgroundColor: "rgba(var(--primary2))",
              },
            },
          },
          "&:hover": {
            "@media (hover: hover) and (pointer: fine)": {
              color: "rgba(var(--textHoverHighlight))",
              border: "1px solid rgba(var(--textHoverHighlight))",
              backgroundColor: "transparent"
            },
          },
          "&:active": {
              color: "rgba(var(--textHoverHighlight))",
              border: "1px solid rgba(var(--textHoverHighlight))",
              backgroundColor: "rgba(var(--primary))"
            }
        }
      }
    },
    MuiYearCalendar: {
      styleOverrides: {
        button: {
          outline: "none",
          color: "rgba(var(--textLight))",
          "&:focus": {
            color: "unset",
            backgroundColor: "unset",
            border: "none"
          },
          "&:focus-visible": {
            color: "rgba(var(--textHoverHighlight))",
            border: "1px solid rgba(var(--textHoverHighlight))",
            backgroundColor: "transparent"
          },
          "&:hover": {
           "@media (hover: hover) and (pointer: fine)": {
              color: "rgba(var(--textHoverHighlight))",
              border: "1px solid rgba(var(--textHoverHighlight))",
              backgroundColor: "transparent"
            }
          },
          "&:active": {
            color: "rgba(var(--textHoverHighlight))",
            border: "1px solid rgba(var(--textHoverHighlight))",
            backgroundColor: "rgba(var(--primary))"
          },
          "&.Mui-selected": {
            color: "rgba(var(--background))",
            border: "1px solid rgba(var(--textHoverHighlight))",
            backgroundColor: "rgba(var(--textHoverHighlight))",
            "&:focus": {
              color: "rgba(var(--background))",
              border: "1px solid rgba(var(--textHoverHighlight))",
              backgroundColor: "rgba(var(--textHoverHighlight))",
            },
            "&:focus-visible": {
              color: "rgba(var(--textHoverHighlight))",
              border: "1px solid rgba(var(--textHoverHighlight))",
              backgroundColor: "rgba(var(--primary2))"
            },
            "&:hover": {
              "@media (hover: hover) and (pointer: fine)": {
                color: "rgba(var(--textHoverHighlight))",
                border: "1px solid rgba(var(--textHoverHighlight))",
                backgroundColor: "rgba(var(--primary2))"
              }
            },
            "&:active": {
              color: "rgba(var(--textHoverHighlight))",
              border: "1px solid rgba(var(--textHoverHighlight))",
              backgroundColor: "rgba(var(--primary))"
            }
          }
        }
      }
    }
  },
});

export default theme;